import React, { useState } from "react";
import { Show, SimpleShowLayout, TextField, useRecordContext } from "react-admin";
import useFetchVideoStat from "../../hooks/useFetchVideoStat";
import {
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    TablePagination,
    Typography,
    CircularProgress,
    Box,
    Chip,
    Avatar
} from "@mui/material";
import { Loader } from "lucide-react";
import { DateUtils } from "../../date.utilitites";
import AvatarField from "../../common/AvatarField";
import { displayDuration } from "../../common/string-utilities";


const DurationAllowed = ({ videoData }: any) => {
    return <div>
        {displayDuration(Math.floor(videoData.maxAllowedDurationInSeconds))}
    </div>
}

const DurationWatchedInSeconds = ({videoData }:any) => {
    let color: 'red' | 'green' | 'orange' = "green";
    if (videoData.maxAllowedDurationInSeconds && ((videoData.maxAllowedDurationInSeconds) * 0.8) < videoData.durationWatchedInSeconds) {
        color = 'orange';
    }
    if (videoData.maxAllowedDurationInSeconds && videoData.maxAllowedDurationInSeconds < videoData.durationWatchedInSeconds) {
        color = 'red';
    }
    return <div className={'text-white px-2 py-0.5 rounded-md text-center'} style={{
        backgroundColor: color
    }}>
        {displayDuration(Math.floor(videoData.durationWatchedInSeconds))}
    </div>
}
const PlaybackWatched = ({videoData}:any) => {
    let color: 'red' | 'green' | 'orange' = "green";
    if (videoData.maxAllowedPlaybacks && ((videoData.maxAllowedPlaybacks) * 0.8) < videoData.currentPlaybacks) {
        color = 'orange';
    }
    if (videoData.maxAllowedPlaybacks && videoData.maxAllowedPlaybacks < videoData.currentPlaybacks) {
        color = 'red';
    }
    return <div className={'text-white px-2 py-0.5 rounded-md text-center'} style={{
        backgroundColor: color
    }}>
        {videoData.currentPlaybacks}
    </div>
}

const UserDetailField = ({ user }: any) => {
    if (!user) return null;

    const getInitials = (firstName?: string, lastName?: string) => {
        const firstInitial = firstName ? firstName[0].toUpperCase() : "";
        const lastInitial = lastName ? lastName[0].toUpperCase() : "";
        return firstInitial + lastInitial;
    };

    return (
        <Typography
            variant="body2"
            display="flex"
            flexWrap="nowrap"
            alignItems="center"
            component="div"
        >
            <Avatar
                src={user.profileImage || undefined}
                sx={{
                    width: 40,
                    height: 40,
                    mr: 1,
                    mt: -0.5,
                    mb: -0.5,
                }}
            >
                {!user.profileImage && getInitials(user.firstName, user.lastName)}
            </Avatar>

            <div className="text-blue-700 cursor-pointer">
                <div>
                    {user.firstName} {user.lastName}
                </div>
                {user.phone && <div>{user.phone}</div>}
                {user.email && <div>{user.email}</div>}
            </div>
        </Typography>
    );
};


const WatchStat = () => {
    const record = useRecordContext<any>();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const { videoStat, total, loading, error } = useFetchVideoStat({
        videoId: record?.id,
        page: page + 1,
        perPage: rowsPerPage,
    });

    if (!record) return <Loader />;
    if (loading)
        return (
            <Box display="flex" justifyContent="center" mt={2}>
                <CircularProgress />
            </Box>
        );
    if (error) return <p style={{ color: "red" }}>Error: {error}</p>;
    if (!Array.isArray(videoStat) || videoStat.length === 0)
        return <p>No watch stats available.</p>;

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const CustomCreatedAt: React.FC<any> = ({ createdAt, updatedAt }) => {
        return (
            <Box display="flex" flexDirection="column" gap={0.5}>
                {createdAt && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <div className="w-4 h-4 flex flex-col items-center rounded-full bg-amber-200">C</div>
                        <p className="text-xs">{DateUtils.formatDateTime(createdAt)}</p>
                    </Box>
                )}
                {updatedAt && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <div className="w-4 h-4 flex flex-col items-center rounded-full bg-pink-200">U</div>
                        <p className="text-xs" >{DateUtils.formatDateTime(updatedAt)}</p>
                    </Box>
                )}
            </Box>
        );
    };

    return (
        <Box mt={2}>

            <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Student</TableCell>
                            <TableCell>Max Allowed Duration</TableCell>
                            <TableCell>Watch Time</TableCell>
                            <TableCell>Max Allowed Playback</TableCell>
                            <TableCell>Playback</TableCell>
                            <TableCell>Current Timestamp</TableCell>
                            <TableCell>Created At</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {videoStat.map((stat: any, idx: number) => (
                            <TableRow key={idx} hover>
                                <TableCell>
                                    <UserDetailField user={stat.user} />
                                </TableCell>
                                <TableCell><DurationAllowed videoData={stat}/></TableCell>
                                <TableCell><DurationWatchedInSeconds videoData={stat}/></TableCell>
                                <TableCell>{stat.maxAllowedPlaybacks}</TableCell>
                                <TableCell><PlaybackWatched videoData={stat}/></TableCell>
                                <TableCell>{stat.currentTimestamp}</TableCell>
                                <TableCell><CustomCreatedAt createdAt={stat.createdAt} updatedAt={stat.updatedAt} /></TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={total || 0}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 20]}
                />
            </TableContainer>
        </Box>
    );
};

const VideoShow = () => {
    return (
        <Show>
            <SimpleShowLayout>
                <TextField source="title" />
                <WatchStat />
            </SimpleShowLayout>
        </Show>
    );
};

export default VideoShow;
